/**
 * Full-gradient promotional banner for the learner home.
 * Used for upsells, announcements, or feature highlights.
 * Dismissable — state managed by parent (reappears on refresh by default).
 */
import { X } from 'lucide-react';

export interface PromotionalBannerProps {
  /** Banner headline */
  title: string;
  /** Supporting description text */
  description: string;
  /** CTA button label */
  ctaLabel: string;
  /** Called when the CTA button is clicked */
  onCtaClick?: () => void;
  /** Called when the banner is dismissed (X button) */
  onDismiss?: () => void;
}

export default function PromotionalBanner({
  title,
  description,
  ctaLabel,
  onCtaClick,
  onDismiss,
}: PromotionalBannerProps) {
  return (
    <div
      className="flex items-center justify-between gap-6 overflow-hidden"
      style={{
        padding: '22px 26px',
        borderRadius: 14,
        background: 'linear-gradient(135deg, #ff9b4a 0%, #ff6eb1 55%, #8a6cf0 100%)',
        color: '#fff',
        position: 'relative',
      }}
    >
      <div className="min-w-0 flex-1">
        <h3
          style={{
            fontSize: 17,
            fontWeight: 600,
            letterSpacing: '-0.015em',
            marginBottom: 5,
          }}
        >
          {title}
        </h3>
        <p style={{ fontSize: 13, opacity: 0.88, lineHeight: 1.5, fontWeight: 400 }}>
          {description}
        </p>
      </div>

      <button
        type="button"
        onClick={onCtaClick}
        className="shrink-0 rounded-full transition-transform duration-150 hover:scale-[1.03] active:scale-[0.97]"
        style={{
          padding: '9px 20px',
          background: 'rgba(255,255,255,0.2)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.25)',
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {ctaLabel}
      </button>

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss banner"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: 'rgba(255,255,255,0.15)',
            border: 'none',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.8)',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.25)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
        >
          <X size={14} strokeWidth={2} />
        </button>
      )}
    </div>
  );
}
